// มี MSE ใช้ hls.js ก่อนเสมอ — Chrome รุ่นใหม่ตอบว่าเล่น HLS เองได้ แต่ native ไม่มี KB/s และ error ละเอียดน้อยกว่า
// native ใช้เมื่อ hls.js ใช้ไม่ได้ (ไม่มี MSE เช่น iPhone)

import React, { useEffect, useRef, useState } from 'react'
import Hls from 'hls.js'
import useProgressWatch, { sampleOf } from '../lib/useProgressWatch'
import { emit, playQuietly, playerCallbackShape } from './playerProps'

// 1 (ยกเลิกเอง) ไม่นับ · 4 = เปิด src ไม่ได้ (403/404/CORS) เพราะเช็ค canPlayType มาแล้ว → ขอ URL ใหม่ได้
const NATIVE_ERROR = { 2: 'networkError', 3: 'mediaError', 4: 'source_failed' }

function canPlayNativeHls(video) {
  return !!video.canPlayType('application/vnd.apple.mpegurl')
}

export default function HlsPlayer({
  url,
  muted,
  onConnecting,
  onPlaying,
  onStall,
  onResume,
  onError,
  onSpeed,
  onAutoMuted,
  onBlocked,
}) {
  const videoRef = useRef(null)
  const hlsRef = useRef(null)
  const nameRef = useRef('hls')
  const [ready, setReady] = useState(false)

  const cb = useRef({})
  cb.current = {
    onConnecting,
    onPlaying,
    onStall,
    onResume,
    onError,
    onSpeed,
    onAutoMuted,
    onBlocked,
  }

  const snapshot = () => ({
    ...sampleOf(videoRef.current),
    player: nameRef.current,
  })

  useEffect(() => {
    const video = videoRef.current
    const fail = (code, detail) => emit(cb, 'onError', code, detail, snapshot())

    // native ไม่มี retry ในตัว ต้องฟัง error ของ <video> เอง ไม่งั้นค้างถาวร (เคยเกิดบน iPhone)
    const onNativeError = () => {
      const code = video.error && NATIVE_ERROR[video.error.code]
      if (code) fail(code, video.error.message || `MediaError ${video.error.code}`)
    }

    const startNative = () => {
      nameRef.current = 'native-hls'
      video.addEventListener('error', onNativeError)
      video.src = url
      setReady(true)
      playQuietly(video, cb)
    }

    const startHlsJs = () => {
      nameRef.current = `hls.js ${Hls.version}`
      const hls = new Hls({ lowLatencyMode: true, enableWorker: true })

      hls.on(Hls.Events.ERROR, (_evt, data) => {
        // เฉพาะ fatal เท่านั้นที่ถือว่ากรอบพัง — non-fatal hls.js กู้เองได้
        if (data && data.fatal) fail(data.type || 'hls_error', data.details)
      })

      // KB/s = ข้อมูลต่อวินาทีของภาพ (เทียบเท่า flv.js) ไม่ใช่ความเร็วดาวน์โหลด — HLS โหลดเป็นก้อน
      // เร็วกว่าเวลาจริงหลายสิบเท่า ถ้าหารด้วยเวลาดาวน์โหลดตัวเลขจะกระโดดเป็นแสน
      hls.on(Hls.Events.FRAG_LOADED, (_evt, data) => {
        const frag = data && data.frag
        const bytes = frag && frag.stats && frag.stats.total
        const sec = frag && frag.duration
        if (bytes > 0 && sec > 0) emit(cb, 'onSpeed', bytes / sec / 1000)
      })

      hls.loadSource(url)
      hls.attachMedia(video)
      hlsRef.current = hls
      setReady(true)
      playQuietly(video, cb)
    }

    emit(cb, 'onConnecting')
    try {
      if (Hls.isSupported()) startHlsJs()
      else if (canPlayNativeHls(video)) startNative()
      else fail('unsupported', 'เบราว์เซอร์นี้เล่น HLS ไม่ได้')
    } catch (err) {
      fail('hls_error', err && err.message)
    }

    return () => {
      setReady(false)
      video.removeEventListener('error', onNativeError)
      const hls = hlsRef.current
      hlsRef.current = null
      if (hls) {
        try {
          hls.destroy()
        } catch (e) {
          /* ถูกทำลายไปแล้ว */
        }
      }
      video.removeAttribute('src')
      try {
        video.load()
      } catch (e) {
        /* ไม่กระทบอะไร */
      }
    }
    // ผูกกับ url อย่างเดียวตามตั้งใจ — callback / snapshot อ่านผ่าน ref เสมอ
  }, [url])

  useProgressWatch(
    videoRef,
    {
      onFirstFrame: () => emit(cb, 'onPlaying', snapshot()),
      onStall: () => emit(cb, 'onStall', snapshot()),
      onResume: () => emit(cb, 'onResume', snapshot()),
    },
    ready,
  )

  return <video ref={videoRef} muted={muted} playsInline autoPlay />
}

HlsPlayer.propTypes = playerCallbackShape
